import { Loader2, X } from "lucide-react"
import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"

import { CohortAvatar } from "components/mentorship/CohortExperience"
import { Icon } from "components/icons/Icon"
import { Button } from "components/ui/button"
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "components/ui/sheet"
import { Textarea } from "components/ui/textarea"
import { Person } from "./types"
import { user as userClient } from "services/api/userClient"

type PersonSidePanelProps = {
  person: Person | null
  onClose: () => void
  onUpdatePerson?: (person: Person) => void
}

const formatDate = (iso?: string | null) => {
  if (!iso) return ""
  const dateOnlyMatch = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (dateOnlyMatch) {
    const year = Number(dateOnlyMatch[1])
    const monthIndex = Number(dateOnlyMatch[2]) - 1
    const day = Number(dateOnlyMatch[3])
    const localDate = new Date(year, monthIndex, day)
    if (Number.isNaN(localDate.getTime())) return ""
    return localDate.toLocaleDateString(undefined, { month: "short", day: "numeric" })
  }
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ""
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" })
}

const formatTodoDue = (iso?: string | null) => {
  const formatted = formatDate(iso)
  return formatted ? `Due: ${formatted}` : ""
}

export function PersonSidePanel({ person, onClose, onUpdatePerson }: PersonSidePanelProps) {
  const [noteValue, setNoteValue] = useState(() => person?.adminNote || "")
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const navigate = useNavigate()

  useEffect(() => {
    setNoteValue(person?.adminNote || "")
    setSaveError(null)
  }, [person?.adminNote, person?.id])

  if (!person) return null

  const handleNavigateProfile = () => {
    navigate(`/profile/${person.id}`)
  }

  const handleSaveNote = async () => {
    setSaving(true)
    setSaveError(null)
    try {
      const resp = await userClient.updateAdminNote(String(person.id), noteValue)
      const adminNote = resp?.admin_note ?? noteValue
      const updated = { ...person, adminNote }
      onUpdatePerson?.(updated)
    } catch (e: any) {
      const msg = e?.response?.data?.detail || e?.message || "Unable to save note"
      setSaveError(msg)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Sheet
      open={Boolean(person)}
      onOpenChange={(open) => {
        if (!open) {
          onClose()
        }
      }}
    >
      <SheetContent overlayClassName="cohort-overlay" className="cohort-experience flex h-full w-full max-w-[460px] flex-col gap-0 overflow-hidden border-l border-[var(--cohort-rule)] bg-[var(--cohort-paper)] p-0 text-[var(--cohort-ink)] shadow-[-24px_0_60px_-20px_rgba(23,26,34,0.5)] [&>button]:hidden">
        <SheetDescription className="sr-only">Review cohort details, tasks, attendance, and private notes for {person.name}.</SheetDescription>
        <header className="shrink-0 border-b border-[var(--cohort-rule)] px-5 pb-3.5 pt-2 sm:px-6 sm:pb-[18px] sm:pt-[22px]">
          <div className="flex items-start gap-3.5">
            <CohortAvatar person={person} size="lg" />
            <div className="min-w-0 flex-1 self-center">
              <SheetTitle className="break-words text-xl font-normal sm:text-2xl">{person.name}</SheetTitle>
              <p className="mt-1 break-words text-[13px] text-[var(--cohort-ink-muted)] sm:text-sm">{person.role}</p>
            </div>
            <button type="button" onClick={onClose} className="cohort-icon-button flex h-11 w-11 shrink-0 items-center justify-center" aria-label="Close member details"><X className="h-5 w-5" /></button>
          </div>
          <button type="button" onClick={handleNavigateProfile} className="cohort-text-action ml-[52px] min-h-11 text-[15px] sm:ml-0">View full profile</button>
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4 sm:px-6 sm:py-5">
          <div className="space-y-[22px]">
            <div>
              <div className="flex items-baseline justify-between gap-3 border-b border-[var(--cohort-rule)] pb-2"><h3 className="text-lg">Open tasks</h3><span className="text-sm text-[var(--cohort-ink-muted)]">{person.taskCompletion.completed} of {person.taskCompletion.total || 0} done</span></div>
              {person.openTodos.length > 0 ? (
                <div className="cohort-bounded-list cohort-bounded-list--compact" role="region" aria-label={`${person.name} open tasks`} tabIndex={0}>
                  {person.openTodos.map((todo) => (
                    <div key={todo.id} className="cohort-ruled-row flex min-h-12 items-center justify-between gap-3 py-2.5 text-[15px]">
                      <p className={todo.completed ? "line-through text-[var(--cohort-ink-muted)]" : ""}>{todo.title}</p>
                      {!todo.completed && formatTodoDue(todo.dueDate) ? <p className="shrink-0 text-sm text-[var(--cohort-ochre)]">{formatTodoDue(todo.dueDate)}</p> : null}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-[var(--cohort-ink-muted)]">No open tasks.</p>
              )}
              <div className="mt-3 flex items-center gap-3.5"><div className="h-[3px] flex-1 bg-[var(--cohort-rule)]"><div className="h-[3px] bg-[var(--cohort-forest)]" style={{ width: `${person.taskCompletion.total ? Math.min(100, (person.taskCompletion.completed / person.taskCompletion.total) * 100) : 0}%` }} /></div><span className="text-sm text-[var(--cohort-ink-muted)]">{person.taskCompletion.total ? Math.round((person.taskCompletion.completed / person.taskCompletion.total) * 100) : 0}%</span></div>
            </div>

            <div>
              <h3 className="border-b border-[var(--cohort-rule)] pb-2 text-lg">Recently attended</h3>
              {person.recentEvents.length > 0 ? (
                <div className="cohort-bounded-list cohort-bounded-list--compact" role="region" aria-label={`${person.name} recently attended events`} tabIndex={0}>
                  {person.recentEvents.map((event) => (
                    <div key={event.id} className="cohort-ruled-row flex min-h-12 items-center justify-between gap-3 py-2.5">
                      <p className="text-[15px]">{event.title}</p>
                      <p className="shrink-0 text-sm text-[var(--cohort-ink-muted)]">{formatDate(event.startTime)}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-[var(--cohort-ink-muted)]">Hasn&apos;t attended an event yet.</p>
              )}
            </div>

            <div>
              <h3 className="border-b border-[var(--cohort-rule)] pb-2 text-lg">Open flags</h3>
              <div className="mt-2.5 flex items-start gap-3 rounded-xl border border-[var(--cohort-rule)] bg-white p-3.5 text-sm leading-5 text-[var(--cohort-ink-muted)]"><Icon name="caution" className="mt-0.5 h-[17px] w-[17px] shrink-0" /><p>Signal details aren&apos;t shown here yet. <a href="#admin-safety-signals" onClick={onClose} className="font-medium text-[var(--cohort-forest)] underline">Open Safety Signals</a> to see this member&apos;s flags.</p></div>
            </div>

            <div>
              <h3 className="border-b border-[var(--cohort-rule)] pb-2 text-lg">Life Score</h3>
              <p className="mt-2.5 text-sm leading-5 text-[var(--cohort-ink-muted)]">Life Score history isn&apos;t available in this member panel yet. Use Reports &amp; trends on the cohort dashboard to review it.</p>
            </div>

            <div>
              <h3 className="border-b border-[var(--cohort-rule)] pb-2 text-lg">Internal note</h3>
              <p className="my-2 text-sm leading-5 text-[var(--cohort-ink-muted)]">Only cohort administrators can read this.</p>
              <Textarea
                placeholder="Add a note about this person..."
                className="min-h-28 rounded-xl border-[var(--cohort-rule)] bg-white text-base"
                value={noteValue}
                onChange={(e) => setNoteValue(e.target.value)}
                disabled={saving}
              />
              {saveError && <p className="mt-2 rounded-xl border border-[var(--cohort-clay-rule)] bg-[var(--cohort-clay-soft)] p-3 text-xs text-[var(--cohort-clay)]">That note didn&apos;t save. What you typed is still in the box.</p>}
            </div>
          </div>
        </div>
        <footer className="shrink-0 border-t border-[var(--cohort-rule)] bg-white px-5 pb-[calc(1rem+env(safe-area-inset-bottom))] pt-3.5 sm:px-6 sm:py-4"><Button className="cohort-primary-button min-h-[52px] w-full text-[17px] sm:min-h-[46px] sm:w-auto sm:text-base" onClick={handleSaveNote} disabled={saving}>{saving ? <><Loader2 className="h-4 w-4 animate-spin" />Saving…</> : "Save note"}</Button></footer>
      </SheetContent>
    </Sheet>
  )
}
